import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import { Loader2, UserPlus, X } from 'lucide-react';

import {
  useFetchDirectSignups,
  useClaimDirectSignup,
} from '../../../../services/onboardingBoard';

// Direct signups (Nic 2026-09-22): veterans who created an account on their
// own with no rep on them. They belong to the Sales Reps like any lead:
// Claim puts them on YOUR board as a potential client waiting on the
// agreement (adopting their old lead when they had one, 9/29); the board
// rings the new row so you can find it.
// Same drawer shell as the Events and Backlog panels.
const DirectSignupsPanel = ({ open, onClose, onClaimed, canClaim = false }) => {
  const { data, isLoading } = useFetchDirectSignups({ enabled: open });
  const claim = useClaimDirectSignup(
    (res) => {
      toast.success('Claimed. They are in your list now.');
      onClaimed?.(res);
    },
    {
      onError: (err) =>
        toast.error(err?.response?.data?.msg || "Couldn't claim that signup."),
    },
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const rows = data?.rows || [];

  return createPortal(
    <div className="fixed inset-0 z-[1060]">
      <div
        className="absolute inset-0 bg-slate-900/30 dark:bg-black/50"
        onClick={onClose}
      />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-[#F5F7FA] shadow-2xl dark:bg-surface-page">
        <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:!bg-surface-elevated">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white">
            <UserPlus size={15} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
              Direct signups
            </div>
            <div className="text-xs text-slate-400">
              Signed up on their own. Claim one to finish their signup.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10"
          >
            <X size={16} />
          </button>
        </div>
        <div className="os-scroll flex-1 space-y-2 overflow-y-auto p-3">
          {isLoading ? (
            <div className="flex justify-center py-6 text-slate-300">
              <Loader2 className="animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-xl bg-white py-6 text-center text-xs text-slate-400 dark:!bg-white/5">
              No direct signups waiting.
            </div>
          ) : (
            rows.map((r) => (
              <div
                key={r.userId}
                className="flex items-center gap-3 rounded-xl bg-white px-3 py-2.5 shadow-sm dark:!bg-white/5"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {`${r.userFirstName || ''} ${r.userLastName || ''}`.trim() || 'Unnamed'}
                  </div>
                  <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                    Signed up {moment(r.createdAt).fromNow()}
                    {r.userPhone ? ` · ${r.userPhone}` : ''}
                  </div>
                  {r.wasLead ? (
                    // Claim adopts this lead (its source and partner), so
                    // the rep knows it is not a brand-new person.
                    <div className="mt-0.5 truncate text-[11px] font-medium text-blue-600 dark:text-blue-300">
                      {r.wasLead.partnerName
                        ? `Was a ${r.wasLead.partnerName} lead`
                        : 'Was a lead'}
                      {r.wasLead.dropped ? ', dropped earlier' : ''}
                    </div>
                  ) : null}
                </div>
                {/* View only can't claim (QA 2026-10-13: it was a 403). */}
                {canClaim ? (
                <button
                  type="button"
                  disabled={claim.isLoading}
                  onClick={() => claim.mutate({ userId: r.userId })}
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50 whitespace-nowrap"
                >
                  {claim.isLoading && claim.variables?.userId === r.userId ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : null}
                  Claim
                </button>
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DirectSignupsPanel;
